import { useEffect, useRef, useState } from "react";
import { MessageCircle, Send, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const GREETING =
    "Hi, I'm Panelo, the AI assistant for The Panel Room. I can answer questions about our mock interview boards or help you book one. What are you preparing for?";
const QUICK_REPLIES = ["Civil Services", "Banking", "State PSC and others"];

/* Panelo — floating AI assistant. Replies come from /api/panelo (managed LLM).
   [LEAD] tags emitted by the model are parsed, stripped and saved as leads
   in the same database/format as the Book form (source: "Panelo chat"). */
const Panelo = () => {
    const [open, setOpen] = useState(false);
    const [greeted, setGreeted] = useState(false);
    const [messages, setMessages] = useState([]);
    const [draft, setDraft] = useState("");
    const [busy, setBusy] = useState(false);
    const sessionId = useRef(
        `web-${Math.random().toString(36).slice(2)}-${Date.now()}`
    );
    const scrollRef = useRef(null);
    const hasLead = useRef(false);

    useEffect(() => {
        if (scrollRef.current) {
            scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        }
    }, [messages, open]);

    const openChat = () => {
        setOpen(true);
        if (!greeted) {
            setGreeted(true);
            setMessages([{ role: "bot", text: GREETING }]);
        }
    };

    const send = async (text) => {
        const clean = text.trim();
        if (!clean || busy) return;
        setMessages((m) => [...m, { role: "user", text: clean }]);
        setDraft("");
        setBusy(true);
        try {
            const res = await fetch(`${API}/panelo`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                credentials: "include",
                body: JSON.stringify({ session_id: sessionId.current, message: clean }),
            });
            const data = await res.json().catch(() => ({}));
            let reply =
                typeof data.reply === "string"
                    ? data.reply
                    : "Our team will confirm that for you. Please try again shortly.";
            // Parse the lead tag the model may append, then strip it from the reply
            const match = reply.match(/\[LEAD\](\{.*\})/);
            if (match) {
                reply = reply.replace(match[0], "").trim();
                try {
                    const lead = JSON.parse(match[1]);
                    const digits = (lead.phone || "").replace(/\D/g, "").slice(-10);
                    if (digits.length === 10 && !hasLead.current) {
                        hasLead.current = true;
                        const saved = await fetch(`${API}/bookings`, {
                            method: "POST",
                            headers: { "Content-Type": "application/json" },
                            credentials: "include",
                            body: JSON.stringify({
                                name: lead.name || "Panelo visitor",
                                phone: digits,
                                exam: lead.exam || "General",
                                slot: lead.slot || "To be confirmed",
                                source: "Panelo chat",
                                note: lead.note || "",
                            }),
                        });
                        if (saved.ok) {
                            setMessages((m) => [
                                ...m,
                                { role: "system", text: "Lead saved — a person will message you on WhatsApp soon." },
                            ]);
                        }
                    }
                } catch {
                    /* malformed tag — ignore, the reply still shows */
                }
            }
            setMessages((m) => [...m, { role: "bot", text: reply }]);
        } catch {
            setMessages((m) => [
                ...m,
                { role: "bot", text: "I'm a little busy right now. Please try again in a moment." },
            ]);
        } finally {
            setBusy(false);
        }
    };

    return (
        <>
            {!open && (
                <button
                    type="button"
                    onClick={openChat}
                    aria-label="Open Panelo chat"
                    data-testid="panelo-toggle"
                    className="fixed bottom-4 right-4 z-[60] flex h-14 w-14 items-center justify-center rounded-full border border-brass bg-pine text-paper shadow-lg transition-colors duration-300 hover:bg-brass"
                >
                    <MessageCircle size={22} strokeWidth={1.5} />
                </button>
            )}

            {open && (
                <div
                    data-testid="panelo-window"
                    className="fixed bottom-4 left-4 right-4 z-[60] flex h-[min(70vh,540px)] flex-col border border-line bg-paper shadow-xl sm:left-auto sm:w-[360px]"
                >
                    <div className="flex items-start justify-between bg-pine px-4 py-3 text-paper">
                        <div>
                            <p className="font-display text-lg leading-tight">Panelo</p>
                            <p className="mt-0.5 text-[11px] text-paper/70">
                                Ask about mock boards, exams or booking.
                            </p>
                        </div>
                        <button
                            type="button"
                            onClick={() => setOpen(false)}
                            aria-label="Close chat"
                            data-testid="panelo-close"
                            className="mt-0.5 text-paper/80 transition-colors duration-300 hover:text-brass"
                        >
                            <X size={18} strokeWidth={1.5} />
                        </button>
                    </div>

                    <div
                        ref={scrollRef}
                        className="flex-1 space-y-3 overflow-y-auto p-4"
                        data-testid="panelo-messages"
                    >
                        {messages.map((m, i) =>
                            m.role === "system" ? (
                                <p
                                    key={i}
                                    className="mx-auto max-w-[90%] border border-brass/50 px-3 py-1.5 text-center text-[11px] uppercase tracking-widest text-brass"
                                >
                                    {m.text}
                                </p>
                            ) : (
                                <p
                                    key={i}
                                    className={`max-w-[85%] px-3 py-2.5 text-sm leading-relaxed ${
                                        m.role === "user"
                                            ? "ml-auto bg-pine text-paper"
                                            : "bg-surface-muted text-ink"
                                    }`}
                                >
                                    {m.text}
                                </p>
                            )
                        )}
                        {busy && (
                            <p className="max-w-[85%] bg-surface-muted px-3 py-2.5 text-sm text-ink-muted">
                                Typing…
                            </p>
                        )}
                        {messages.length === 1 && (
                            <div className="flex flex-wrap gap-2 pt-1">
                                {QUICK_REPLIES.map((q) => (
                                    <button
                                        key={q}
                                        type="button"
                                        data-testid={`panelo-quick-${q.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                                        onClick={() => send(q)}
                                        className="border border-brass px-3 py-1.5 text-xs text-brass transition-colors duration-300 hover:bg-brass hover:text-white"
                                    >
                                        {q}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    <form
                        onSubmit={(e) => {
                            e.preventDefault();
                            send(draft);
                        }}
                        className="flex items-center gap-2 border-t border-line bg-surface px-3 py-2.5"
                    >
                        <input
                            type="text"
                            value={draft}
                            onChange={(e) => setDraft(e.target.value)}
                            placeholder="Type your question…"
                            data-testid="panelo-input"
                            aria-label="Message Panelo"
                            className="field py-2 text-sm"
                        />
                        <button
                            type="submit"
                            disabled={busy || !draft.trim()}
                            aria-label="Send message"
                            data-testid="panelo-send"
                            className="flex h-9 w-9 shrink-0 items-center justify-center bg-pine text-paper transition-colors duration-300 hover:bg-brass disabled:opacity-60"
                        >
                            <Send size={15} strokeWidth={1.5} />
                        </button>
                    </form>
                </div>
            )}
        </>
    );
};

export default Panelo;
